// Unpublished Work © 2023-2024 Deere & Company.

import * as actions from '../../action-list';
import {DataShare} from '../../state/struct/add-customer/data-share';
import {contactPhoneFieldKeys} from '../../state/struct/add-customer/field-keys';
import {Phone} from '../../state/struct/customer/phone';
import {PageStatus} from '../../state/struct/page-status';
import {DataShareSaveSuccess} from '../../state/struct/add-customer/data-share-save-success';
import {SaveError} from '../../state/struct/add-customer/save-error';

const setDataShareCustomer = (state, action) =>
    DataShare.update(state, {
        customer: {
            $set: action.dataShareCustomer
        },
        labels: {
            $set: action.dbsCustomerProfile.dbsLabels
        }
    });

const setDbsCustomerDataShareCustomer = (state, action) => DataShare.update(state, {
    customer: {
        customerName: {
            [action.fieldKey]: {
                $set: action.value
            }
        }
    }
});

const setDbsCustomerNameValues = (state, action) =>
    DataShare.update(state, {
        customer: {
            customerName: {
                $merge: action.values
            }
        }
    });

const setDbsCustomerDataSharePhysicalAddress = (state, action) => DataShare.update(state, {
    customer: {
        communication: {
            physicalAddress: {
                [action.fieldKey]: {
                    $set: action.value
                }
            }
        }
    }
});

const setDbsCustomerDataShareMailingAddress = (state, action) => DataShare.update(state, {
    customer: {
        communication: {
            mailingAddress: {
                [action.fieldKey]: {
                    $set: action.value
                }
            }
        }
    }
});

const setContactInfoPhone = (state, action) =>
    DataShare.update(state, {
        customer: {
            communication: {
                [action.fieldKey]: {
                    [action.contactPhoneFieldKey]: {
                        $set: action.value
                    }
                }
            }
        }
    });

const setContactInfoPhoneAll = (state, action) =>
    DataShare.update(state, {
        customer: {
            communication: {
                [action.fieldKey]: {
                    [contactPhoneFieldKeys.AREA_CODE]: {
                        $set: action.value[contactPhoneFieldKeys.AREA_CODE]
                    },
                    [contactPhoneFieldKeys.EXTENSION]: {
                        $set: action.value[contactPhoneFieldKeys.EXTENSION]
                    },
                    [contactPhoneFieldKeys.PHONE]: {
                        $set: action.value[contactPhoneFieldKeys.PHONE]
                    },
                    [contactPhoneFieldKeys.COUNTRY_CODE]: {
                        $set: action.value[contactPhoneFieldKeys.COUNTRY_CODE]
                    },
                    [contactPhoneFieldKeys.FORMATTED_PHONE]: {
                        $set: action.value[contactPhoneFieldKeys.FORMATTED_PHONE]
                    }
                }
            }
        }
    });

const resetContactPhone = (state, action) =>
    DataShare.update(state, {
        customer: {
            communication: {
                [action.fieldKey]: {
                    $set: new Phone({
                        countryCode: action.defaultCountry
                    })
                }
            }
        }
    });

const setEmailAddress = (state, action) => DataShare.update(state, {
    customer: {
        communication: {
            email: {
                emailAddress: {
                    $set: action.value
                }
            }
        }
    }
});

const setAllEmailAddress = (state, action) => DataShare.update(state, {
    customer: {
        communication: {
            email: {
                $set: action.value
            }
        }
    }
});

const setEmailNotAvailableCheck = (state, action) =>
    DataShare.update(state, {
        customer: {
            communication: {
                email: {
                    emailNotAvailableCheckSelected: {
                        $set: action.isEmailNotAvailable
                    }
                }
            }
        }
    });

const updateDataShareCustomerCommunication = (state, fieldKey, newValue) =>
    DataShare.update(state, {
        customer: {
            communication: {
                $merge: {
                    [fieldKey]: newValue
                }
            }
        }
    });

const resetDbsCustomerAddress = (state, action) => updateDataShareCustomerCommunication(state, action.fieldKey, action.payload);

const setCountryErrorState = (state, action) => DataShare.update(state, {
    dataShareApplicationStatus: {
        canShowCountryServiceError: {
            $set: action.showError
        }
    }
});

const setDataSharePageStatus = (state, action) => DataShare.update(state, {
    dataShareApplicationStatus: {
        dataSharePageStatus: {
            $set: action.pageStatus
        }
    }
});

const setDataShareErrorStatus = (state) => DataShare.update(state, {
    dataShareApplicationStatus: {
        dataSharePageStatus: {
            $set: PageStatus.meta.map.ERROR
        },
        canResetForm: {
            $set: false
        }
    }
});

const setDataShareSaveServiceError = (state, action) => DataShare.update(state, {
    dataShareApplicationStatus: {
        dataShareSaveError: {
            $set: action.saveError
        }
    }
});

const setDataShareSaveSuccess = (state, action) => DataShare.update(state, {
    dataShareApplicationStatus: {
        dataShareSaveSuccess: {
            $merge: action.dataShareSaveSuccess
        }
    }
});

const resetSuccessErrorMessages = (state) => DataShare.update(state, {
    dataShareApplicationStatus: {
        dataShareSaveError: {
            $set: new SaveError({})
        },
        dataShareSaveSuccess: {
            $set: new DataShareSaveSuccess({})
        }
    }
});

export const dataShare = (state = DataShare({}), action = {}) => {
    const actionHandlers = {
        [actions.RESET_DATA_SHARE_CONTACT_PHONE]: resetContactPhone,
        [actions.SET_DBS_CUSTOMER_PROFILE_DATA]: setDataShareCustomer,
        [actions.SET_DBS_CUSTOMER_NAME_DETAILS]: setDbsCustomerDataShareCustomer,
        [actions.SET_CUSTOMER_PREFIX_AND_GENERATION_DETAILS_FOR_DBS]: setDbsCustomerNameValues,
        [actions.SET_DBS_CUSTOMER_CONTACT_PHONE]: setContactInfoPhone,
        [actions.SET_DBS_CUSTOMER_CONTACT_PHONE_ALL]: setContactInfoPhoneAll,
        [actions.SET_DBS_CUSTOMER_PHYSICAL_ADDRESS]: setDbsCustomerDataSharePhysicalAddress,
        [actions.SET_DBS_CUSTOMER_MAILING_ADDRESS]: setDbsCustomerDataShareMailingAddress,
        [actions.SET_DBS_CUSTOMER_EMAIL_INFO]: setEmailAddress,
        [actions.SET_DBS_EMAIL_NOT_AVAILABLE_CHECK]: setEmailNotAvailableCheck,
        [actions.SET_DBS_ALL_CUSTOMER_EMAIL_INFO]: setAllEmailAddress,
        [actions.RESET_ADD_CUSTOMER_FORM]: () => DataShare({}),
        [actions.RESET_DBS_CUSTOMER_ADDRESS]: resetDbsCustomerAddress,
        [actions.SET_COUNTRY_SELECTION_ERROR_ON_DATA_SHARE]: setCountryErrorState,
        [actions.SET_DBS_CUSTOMER_PROFILE_PAGE_STATUS]: setDataSharePageStatus,
        [actions.SET_DBS_CUSTOMER_PROFILE_ERROR]: setDataShareErrorStatus,
        [actions.SET_DBS_SAVE_SERVICE_ERROR]: setDataShareSaveServiceError,
        [actions.SET_DATA_SHARE_SAVE_SUCCESS]: setDataShareSaveSuccess,
        [actions.RESET_DATA_SHARE_SAVE_SUCCESS_MESSAGE]: setDataShareSaveSuccess,
        [actions.RESET_DATA_SHARE_SAVE_ERROR_MESSAGE]: setDataShareSaveServiceError,
        [actions.RESET_REGISTRY_SUCCESS_MESSAGE]: setDataShareSaveSuccess,
        [actions.RESET_ALERT_MESSAGES]: resetSuccessErrorMessages
    };
    const reducer = actionHandlers[action.type];

    return reducer ? reducer(state, action) : state;
};
